/**
 * Triple slideshow hero
 * One instance per panel. Slides live in a native scroll-snap track, so swiping
 * works on its own — this script adds autoplay, arrows and dots on top of it.
 */
(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeroSlideshowPanel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-htsl-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-htsl-slide]'));
      if (this.slides.length < 2) return;

      this.dots = Array.from(this.querySelectorAll('[data-htsl-dot]'));
      this.prevButton = this.querySelector('[data-htsl-prev]');
      this.nextButton = this.querySelector('[data-htsl-next]');

      this.index = 0;
      this.paused = false;
      this.visible = true;
      this.interval = (parseFloat(this.dataset.interval) || 6) * 1000;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !reducedMotion.matches;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);

      this.bindEvents();
      this.syncUI();
      this.play();
    }

    disconnectedCallback() {
      this.pause();
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    bindEvents() {
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.goTo(this.index - 1);
          this.restart();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.goTo(this.index + 1);
          this.restart();
        });
      }

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.goTo(i);
          this.restart();
        });
      });

      // Hold still while someone is looking at, hovering or swiping a slide.
      ['mouseenter', 'focusin', 'touchstart', 'pointerdown'].forEach((event) => {
        this.addEventListener(event, () => { this.paused = true; }, { passive: true });
      });
      ['mouseleave', 'focusout', 'touchend', 'pointerup'].forEach((event) => {
        this.addEventListener(event, () => { this.paused = false; }, { passive: true });
      });

      // Only run while the section is actually on screen.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => { this.visible = entries[0].isIntersecting; },
          { threshold: 0.2 }
        );
        this.observer.observe(this);
      }

      // Keep the current slide aligned when the panel is resized.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.handleResize);
      }

      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    goTo(target, instant) {
      const count = this.slides.length;
      const next = ((target % count) + count) % count;
      this.index = next;
      this.track.scrollTo({
        left: this.slides[next].offsetLeft,
        behavior: instant || reducedMotion.matches ? 'auto' : 'smooth'
      });
      this.syncUI();
    }

    syncUI() {
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const width = this.track.clientWidth || 1;
        const index = Math.round(this.track.scrollLeft / width);
        if (index !== this.index && this.slides[index]) {
          this.index = index;
          this.syncUI();
        }
      });
    }

    handleResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: 'auto' });
      });
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const index = this.slides.indexOf(event.target);
      if (index > -1) this.goTo(index);
      this.paused = true;
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.paused = false;
    }

    play() {
      if (!this.autoplayEnabled) return;
      this.pause();
      this.timer = setInterval(() => {
        if (this.paused || !this.visible || document.hidden) return;
        this.goTo(this.index + 1);
      }, this.interval);
    }

    pause() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.play();
    }
  }

  if (!customElements.get('hero-slideshow-panel')) {
    customElements.define('hero-slideshow-panel', HeroSlideshowPanel);
  }
})();